import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { X, Link as LinkIcon, Upload, ExternalLink } from 'lucide-react';
import {
  createAssignment,
  getAssignments,
  updateAssignment,
  deleteAssignment
} from '../firebase/assignmentService';
import { getUnits } from '../firebase/lessonPlannerService';
import { getLessons } from '../firebase/lessonPlannerService';
import { uploadAssignmentResource } from '../firebase/storageService';
import DashboardLayout from './DashboardLayout';
import './Assignments.css';

function Assignments() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [assignments, setAssignments] = useState([]);
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [filterUnit, setFilterUnit] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGradeCategory, setFilterGradeCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [uploadingFile, setUploadingFile] = useState(null);

  // Helper function to parse date string as local time
  const parseDateLocal = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const [assignmentForm, setAssignmentForm] = useState({
    title: '',
    description: '',
    instructions: '',
    assignmentDate: '',
    dueDate: '',
    points: '',
    gradeCategory: 'Major',
    unitId: '',
    lessonId: '',
    resources: []
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentUser, navigate]);

  const loadData = async () => {
    setLoading(true);
    const [assignmentsResult, unitsResult, lessonsResult] = await Promise.all([
      getAssignments(currentUser.uid),
      getUnits(currentUser.uid),
      getLessons(currentUser.uid)
    ]);

    if (assignmentsResult.success) {
      setAssignments(assignmentsResult.assignments);
    }
    if (unitsResult.success) {
      setUnits(unitsResult.units);
    }
    if (lessonsResult.success) {
      setLessons(lessonsResult.lessons);
    }
    setLoading(false);
  };

  const handleCreateAssignment = () => {
    setEditingAssignment(null);
    setAssignmentForm({
      title: '',
      description: '',
      instructions: '',
      assignmentDate: '',
      dueDate: '',
      points: '',
      gradeCategory: 'Major',
      unitId: '',
      lessonId: '',
      resources: []
    });
    setShowModal(true);
  };

  const handleEditAssignment = (assignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      title: assignment.title || '',
      description: assignment.description || '',
      instructions: assignment.instructions || '',
      assignmentDate: assignment.assignmentDate || '',
      dueDate: assignment.dueDate || '',
      points: assignment.points || '',
      gradeCategory: assignment.gradeCategory || 'Major',
      unitId: assignment.unitId || '',
      lessonId: assignment.lessonId || '',
      resources: assignment.resources || []
    });
    setShowModal(true);
  };

  const handleSaveAssignment = async (e) => {
    e.preventDefault();

    if (!assignmentForm.title) {
      alert('Please enter an assignment title');
      return;
    }

    const assignmentData = {
      ...assignmentForm,
      points: assignmentForm.points ? parseInt(assignmentForm.points) : null
    };

    let result;
    if (editingAssignment) {
      result = await updateAssignment(editingAssignment.id, assignmentData);
    } else {
      result = await createAssignment(currentUser.uid, assignmentData);
    }

    if (result.success) {
      await loadData();
      closeModal();
    } else {
      alert('Error saving assignment. Please try again.');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (window.confirm('Are you sure you want to delete this assignment?')) {
      const result = await deleteAssignment(assignmentId);
      if (result.success) {
        await loadData();
      } else {
        alert('Error deleting assignment. Please try again.');
      }
    }
  };

  const addResource = () => {
    setAssignmentForm({
      ...assignmentForm,
      resources: [
        ...assignmentForm.resources,
        { description: '', type: 'link', url: '', fileName: '', fileUrl: '' }
      ]
    });
  };

  const removeResource = (index) => {
    const newResources = assignmentForm.resources.filter((_, i) => i !== index);
    setAssignmentForm({ ...assignmentForm, resources: newResources });
  };

  const updateResource = (index, field, value) => {
    const newResources = [...assignmentForm.resources];
    newResources[index] = { ...newResources[index], [field]: value };
    setAssignmentForm({ ...assignmentForm, resources: newResources });
  };

  const handleFileUpload = async (index, file) => {
    if (!file) return;

    setUploadingFile(index);

    // For new assignments, we'll use a temporary ID
    const assignmentId = editingAssignment?.id || 'temp_' + Date.now();
    const userId = currentUser.uid;

    const result = await uploadAssignmentResource(userId, assignmentId, file);

    if (result.success) {
      const newResources = [...assignmentForm.resources];
      newResources[index] = {
        ...newResources[index],
        fileUrl: result.url,
        fileName: result.fileName,
        filePath: result.filePath
      };
      setAssignmentForm({ ...assignmentForm, resources: newResources });
    } else {
      alert('Error uploading file. Please try again.');
    }

    setUploadingFile(null);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingAssignment(null);
  };

  const getUnitTitle = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit?.title || 'Unknown Unit';
  };

  const getLessonTitle = (lessonId) => {
    const lesson = lessons.find(l => l.id === lessonId);
    return lesson?.title || 'Unknown Lesson';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = parseDateLocal(dueDate);
    if (!due) return false;
    return due < today;
  };

  const isDueThisWeek = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    const due = parseDateLocal(dueDate);
    if (!due) return false;
    return due >= today && due <= weekFromNow;
  };

  const filteredAssignments = assignments.filter(assignment => {
    const matchesSearch = assignment.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assignment.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesUnit = filterUnit === 'all' || assignment.unitId === filterUnit;
    const matchesGradeCategory = filterGradeCategory === 'all' || assignment.gradeCategory === filterGradeCategory;

    let matchesStatus = true;
    if (filterStatus === 'overdue') {
      matchesStatus = isOverdue(assignment.dueDate);
    } else if (filterStatus === 'upcoming') {
      matchesStatus = isDueThisWeek(assignment.dueDate);
    } else if (filterStatus === 'standalone') {
      matchesStatus = !assignment.unitId && !assignment.lessonId;
    }

    return matchesSearch && matchesUnit && matchesStatus && matchesGradeCategory;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assignments...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="assignments-container">
        <div className="assignments-header">
          <div>
            <h1>Assignments</h1>
            <p className="subtitle">Manage and track student assignments</p>
          </div>
          <button onClick={handleCreateAssignment} className="create-btn">
            + New Assignment
          </button>
        </div>

        <div className="assignments-controls">
          <div className="assignments-filters">
            <input
              type="text"
              placeholder="Search assignments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />

            <select
              value={filterUnit}
              onChange={(e) => setFilterUnit(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Units</option>
              {units.map(unit => (
                <option key={unit.id} value={unit.id}>{unit.title}</option>
              ))}
            </select>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Assignments</option>
              <option value="overdue">Overdue</option>
              <option value="upcoming">Due This Week</option>
              <option value="standalone">Standalone</option>
            </select>

            <select
              value={filterGradeCategory}
              onChange={(e) => setFilterGradeCategory(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Categories</option>
              <option value="Major">Major Grades</option>
              <option value="Minor">Minor Grades</option>
            </select>
          </div>

          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              📊 Grid
            </button>
            <button
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              📋 List
            </button>
          </div>
        </div>

        <div className="assignments-stats">
          <div className="stat-card">
            <div className="stat-number">{assignments.length}</div>
            <div className="stat-label">Total Assignments</div>
          </div>
          <div className="stat-card overdue">
            <div className="stat-number">
              {assignments.filter(a => isOverdue(a.dueDate)).length}
            </div>
            <div className="stat-label">Overdue</div>
          </div>
          <div className="stat-card upcoming">
            <div className="stat-number">
              {assignments.filter(a => isDueThisWeek(a.dueDate)).length}
            </div>
            <div className="stat-label">Due This Week</div>
          </div>
          <div className="stat-card standalone">
            <div className="stat-number">
              {assignments.filter(a => !a.unitId && !a.lessonId).length}
            </div>
            <div className="stat-label">Standalone</div>
          </div>
        </div>

        {filteredAssignments.length === 0 ? (
          <div className="empty-state">
            <h2>No Assignments Found</h2>
            <p>Create your first assignment to get started</p>
            <button onClick={handleCreateAssignment} className="create-btn-large">
              + Create Assignment
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="assignments-grid">
            {filteredAssignments.map(assignment => (
              <div
                key={assignment.id}
                className={`assignment-card ${isOverdue(assignment.dueDate) ? 'overdue' : ''} ${isDueThisWeek(assignment.dueDate) ? 'upcoming' : ''}`}
              >
                <div className="assignment-header">
                  <h3>{assignment.title}</h3>
                  <div className="assignment-actions">
                    <button onClick={() => handleEditAssignment(assignment)} className="edit-icon">
                      ✏️
                    </button>
                    <button onClick={() => handleDeleteAssignment(assignment.id)} className="delete-icon">
                      🗑️
                    </button>
                  </div>
                </div>

                {assignment.description && (
                  <p className="assignment-description">{assignment.description}</p>
                )}

                <div className="assignment-meta">
                  {assignment.assignmentDate && (
                    <div className="meta-item">
                      <span className="meta-label">Assigned:</span>
                      <span className="meta-value">
                        {parseDateLocal(assignment.assignmentDate)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}

                  {assignment.dueDate && (
                    <div className="meta-item">
                      <span className="meta-label">Due:</span>
                      <span className={`meta-value ${isOverdue(assignment.dueDate) ? 'overdue-text' : ''}`}>
                        {parseDateLocal(assignment.dueDate)?.toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}

                  {assignment.points && (
                    <div className="meta-item">
                      <span className="meta-label">Points:</span>
                      <span className="meta-value">{assignment.points}</span>
                    </div>
                  )}

                  {assignment.gradeCategory && (
                    <div className="meta-item">
                      <span className={`grade-badge ${assignment.gradeCategory.toLowerCase()}`}>
                        {assignment.gradeCategory}
                      </span>
                    </div>
                  )}
                </div>

                <div className="assignment-tags">
                  {assignment.unitId && (
                    <span className="tag unit-tag">{getUnitTitle(assignment.unitId)}</span>
                  )}
                  {assignment.lessonId && (
                    <span className="tag lesson-tag">{getLessonTitle(assignment.lessonId)}</span>
                  )}
                  {!assignment.unitId && !assignment.lessonId && (
                    <span className="tag standalone-tag">Standalone</span>
                  )}
                </div>

                {assignment.resources && assignment.resources.length > 0 && (
                  <div className="assignment-resources">
                    <strong>Resources:</strong>
                    <div className="resources-links">
                      {assignment.resources.map((resource, idx) => {
                        const resourceUrl = resource.fileUrl || resource.url;
                        return resourceUrl ? (
                          <a
                            key={idx}
                            href={resourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="resource-link"
                          >
                            <ExternalLink size={14} />
                            <span>{resource.description || `Resource ${idx + 1}`}</span>
                          </a>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="assignments-list-view">
            <table className="assignments-table">
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th>Grade Category</th>
                  <th>Assigned Date</th>
                  <th>Due Date</th>
                  <th>Points</th>
                  <th>Unit</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments
                  .sort((a, b) => {
                    // Sort by due date, then assignment date
                    const dateA = new Date(a.dueDate || a.assignmentDate || '9999-12-31');
                    const dateB = new Date(b.dueDate || b.assignmentDate || '9999-12-31');
                    return dateA - dateB;
                  })
                  .map(assignment => (
                    <tr
                      key={assignment.id}
                      className={`assignment-row ${isOverdue(assignment.dueDate) ? 'overdue' : ''} ${isDueThisWeek(assignment.dueDate) ? 'upcoming' : ''}`}
                    >
                      <td className="assignment-title-cell">
                        <div className="title-with-desc">
                          <strong>{assignment.title}</strong>
                          {assignment.description && (
                            <small className="description-preview">{assignment.description}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`grade-badge ${assignment.gradeCategory?.toLowerCase() || 'major'}`}>
                          {assignment.gradeCategory || 'Major'}
                        </span>
                      </td>
                      <td>
                        {assignment.assignmentDate ? (
                          parseDateLocal(assignment.assignmentDate)?.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        ) : (
                          <span className="no-date">—</span>
                        )}
                      </td>
                      <td>
                        {assignment.dueDate ? (
                          <span className={isOverdue(assignment.dueDate) ? 'overdue-text' : ''}>
                            {parseDateLocal(assignment.dueDate)?.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        ) : (
                          <span className="no-date">—</span>
                        )}
                      </td>
                      <td>{assignment.points || '—'}</td>
                      <td>
                        {assignment.unitId ? (
                          <span className="unit-label">{getUnitTitle(assignment.unitId)}</span>
                        ) : (
                          <span className="standalone-label">Standalone</span>
                        )}
                      </td>
                      <td>
                        {isOverdue(assignment.dueDate) ? (
                          <span className="status-badge overdue">Overdue</span>
                        ) : isDueThisWeek(assignment.dueDate) ? (
                          <span className="status-badge upcoming">Due Soon</span>
                        ) : (
                          <span className="status-badge active">Active</span>
                        )}
                      </td>
                      <td className="actions-cell">
                        <button
                          onClick={() => handleEditAssignment(assignment)}
                          className="action-btn edit-btn"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteAssignment(assignment.id)}
                          className="action-btn delete-btn"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content assignment-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>×</button>
              <h2>{editingAssignment ? 'Edit Assignment' : 'New Assignment'}</h2>

              <form onSubmit={handleSaveAssignment} className="assignment-form">
                <div className="form-group">
                  <label htmlFor="title">Assignment Title *</label>
                  <input
                    type="text"
                    id="title"
                    value={assignmentForm.title}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })}
                    placeholder="e.g., Chapter 5 Quiz"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={assignmentForm.description}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })}
                    placeholder="Brief description of the assignment"
                    rows="2"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="assignmentDate">Assignment Date</label>
                    <input
                      type="date"
                      id="assignmentDate"
                      value={assignmentForm.assignmentDate}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignmentDate: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="dueDate">Due Date</label>
                    <input
                      type="date"
                      id="dueDate"
                      value={assignmentForm.dueDate}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, dueDate: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="points">Points</label>
                    <input
                      type="number"
                      id="points"
                      value={assignmentForm.points}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, points: e.target.value })}
                      placeholder="100"
                      min="0"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="gradeCategory">Grade Category *</label>
                    <select
                      id="gradeCategory"
                      value={assignmentForm.gradeCategory}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, gradeCategory: e.target.value })}
                      required
                    >
                      <option value="Major">Major Grade</option>
                      <option value="Minor">Minor Grade</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="unitId">Assign to Unit (optional)</label>
                    <select
                      id="unitId"
                      value={assignmentForm.unitId}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, unitId: e.target.value })}
                    >
                      <option value="">None - Standalone</option>
                      {units.map(unit => (
                        <option key={unit.id} value={unit.id}>{unit.title}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="lessonId">Attach to Lesson (optional)</label>
                    <select
                      id="lessonId"
                      value={assignmentForm.lessonId}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, lessonId: e.target.value })}
                    >
                      <option value="">None</option>
                      {lessons
                        .filter(l => !assignmentForm.unitId || l.unitId === assignmentForm.unitId)
                        .map(lesson => (
                          <option key={lesson.id} value={lesson.id}>{lesson.title}</option>
                        ))}
                    </select>
                    {assignmentForm.unitId && (
                      <small>Only showing lessons from selected unit</small>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="instructions">Instructions</label>
                  <textarea
                    id="instructions"
                    value={assignmentForm.instructions}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, instructions: e.target.value })}
                    placeholder="Detailed instructions for students"
                    rows="5"
                  />
                </div>

                {/* Resources Section */}
                <div className="form-group array-field">
                  <label>Resources</label>
                  <small className="field-hint">Add links or upload files for students to reference</small>
                  {assignmentForm.resources && assignmentForm.resources.length > 0 && (
                    <div className="resources-list">
                      {assignmentForm.resources.map((resource, index) => (
                        <div key={index} className="resource-item">
                          <div className="resource-controls">
                            <input
                              type="text"
                              value={resource.description}
                              onChange={(e) => updateResource(index, 'description', e.target.value)}
                              placeholder="Resource description"
                              className="resource-description"
                            />
                            <select
                              value={resource.type}
                              onChange={(e) => updateResource(index, 'type', e.target.value)}
                              className="resource-type-select"
                            >
                              <option value="link">Link</option>
                              <option value="file">File Upload</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => removeResource(index)}
                              className="btn-icon-small delete"
                            >
                              <X size={16} />
                            </button>
                          </div>
                          {resource.type === 'file' ? (
                            <div className="file-upload-section">
                              {resource.fileUrl ? (
                                <div className="file-uploaded">
                                  <span>✓ {resource.fileName}</span>
                                  <a href={resource.fileUrl} target="_blank" rel="noopener noreferrer" className="view-file-btn">
                                    View File
                                  </a>
                                </div>
                              ) : (
                                <div className="file-input-wrapper">
                                  <Upload size={16} />
                                  <input
                                    type="file"
                                    onChange={(e) => handleFileUpload(index, e.target.files[0])}
                                    disabled={uploadingFile === index}
                                    className="file-input"
                                  />
                                  {uploadingFile === index && <span className="uploading-text">Uploading...</span>}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="url-input-wrapper">
                              <LinkIcon size={16} className="url-icon" />
                              <input
                                type="url"
                                value={resource.url}
                                onChange={(e) => updateResource(index, 'url', e.target.value)}
                                placeholder="https://example.com/resource"
                                className="resource-url"
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={addResource}
                    className="add-field-btn"
                  >
                    + Add Resource
                  </button>
                </div>

                <div className="modal-buttons">
                  <button type="submit" className="save-button">
                    {editingAssignment ? 'Update' : 'Create'} Assignment
                  </button>
                  <button type="button" onClick={closeModal} className="cancel-button">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

export default Assignments;
